import React from "react";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import { useAxesTooltip, ChartsTooltipContainer } from "@mui/x-charts/ChartsTooltip";
import { getMetricRagStatus, getRagColor } from "../../utils";

/**
 * Lets the tooltip (a MUI slot, which receives no props) read the chart's raw
 * rows so it can surface per-point min/max that live as `${dataKey}_min/_max`
 * fields rather than as plotted series.
 */
interface TooltipDataContextValue {
  data: Array<Record<string, unknown>>;
  xAxisKey: string;
  seriesWithMinMax: Set<string>;
}
export const ChartTooltipDataContext =
  React.createContext<TooltipDataContextValue | null>(null);

const formatBound = (value: unknown): string | null => {
  if (typeof value !== "number" || !isFinite(value)) return null;
  return String(Math.round(value * 10) / 10);
};

/**
 * Custom axis tooltip for trend charts. For each series under the cursor it
 * shows the series label, the formatted value, and the RAG band that value
 * falls into (e.g. "Recovery 72% — 🟢 Good), so a hovered point carries a
 * judgement, not just a number. Metrics with no agreed band (e.g. HRV) simply
 * show the value with no status dot.
 */
const ChartMetricTooltipContent: React.FC = () => {
  const theme = useTheme();
  const tooltipData = useAxesTooltip();
  const minMaxCtx = React.useContext(ChartTooltipDataContext);

  if (!tooltipData || tooltipData.length === 0) return null;

  // Find the raw data row under the cursor (matched on the x-axis value) so we
  // can read per-point min/max bounds that aren't part of the plotted series.
  const findRow = (axisValue: unknown): Record<string, unknown> | null => {
    if (!minMaxCtx) return null;
    const key = minMaxCtx.xAxisKey;
    return (
      minMaxCtx.data.find((d) => String(d[key]) === String(axisValue)) ?? null
    );
  };

  return (
    <Paper
      elevation={3}
      sx={{ p: 1.25, minWidth: 160, backgroundColor: theme.palette.background.paper }}
    >
      {tooltipData.map((axis) => (
        <Box key={String(axis.axisId)}>
          {axis.axisValue != null && (
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 0.5 }}
            >
              {String(axis.axisFormattedValue ?? axis.axisValue)}
            </Typography>
          )}
          {(() => {
            const row = findRow(axis.axisValue);
            return axis.seriesItems.map((item) => {
              const label = String(item.formattedLabel ?? item.seriesId ?? "");
              const numericValue =
                typeof item.value === "number" ? item.value : null;
              const status = getMetricRagStatus(label, numericValue);

              const dataKey = String(item.seriesId ?? "");
              const showBounds =
                row && minMaxCtx?.seriesWithMinMax.has(dataKey);
              const minStr = showBounds ? formatBound(row?.[`${dataKey}_min`]) : null;
              const maxStr = showBounds ? formatBound(row?.[`${dataKey}_max`]) : null;

              return (
                <Box key={String(item.seriesId)} sx={{ py: 0.25 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {/* Series colour swatch */}
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "2px",
                        flexShrink: 0,
                        backgroundColor: item.color,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1, mr: 1 }}>
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {String(item.formattedValue ?? item.value ?? "")}
                    </Typography>
                    {/* RAG status (only when the metric has an agreed band) */}
                    {status.label && status.ragColor && (
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, ml: 0.5 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: getRagColor(status.ragColor, theme),
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, color: getRagColor(status.ragColor, theme) }}
                        >
                          {status.label}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {/* Per-point min/max range (when the series carries bounds) */}
                  {(minStr !== null || maxStr !== null) && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block", ml: 2.25 }}
                    >
                      range {minStr ?? "—"}–{maxStr ?? "—"}
                    </Typography>
                  )}
                </Box>
              );
            });
          })()}
        </Box>
      ))}
    </Paper>
  );
};

/** Drop-in `slots.tooltip` for MUI X charts using axis trigger. */
export const ChartMetricTooltip: React.FC = () => (
  <ChartsTooltipContainer trigger="axis">
    <ChartMetricTooltipContent />
  </ChartsTooltipContainer>
);
